import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Smartphone, Globe, Shield, Zap, Copy, Check, Code2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEMO_KEY = "ipplus_web_2f8c1a9d4e6b7c3a5f0d8e2b1c4a6f9d";

export default function Home() {
  const [copied, setCopied] = useState(false);

  const snippet =
`<script src="${BACKEND_URL}/ipplus-chat-widget.js"
        data-api="${BACKEND_URL}"
        data-key="ВАШ_API_КЛЮЧ"
        data-source="web"
        data-color="#0F76BC"></script>`;

  useEffect(() => {
    // live demo widget
    if (document.getElementById("ippc-demo-script")) return;
    const s = document.createElement("script");
    s.id = "ippc-demo-script";
    s.src = `${BACKEND_URL}/ipplus-chat-widget.js`;
    s.setAttribute("data-api", BACKEND_URL);
    s.setAttribute("data-key", DEMO_KEY);
    s.setAttribute("data-source", "web");
    s.setAttribute("data-color", "#0F76BC");
    document.body.appendChild(s);
  }, []);

  const copy = () => {
    navigator.clipboard.writeText(snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const features = [
    { icon: Globe, title: "Універсальний", text: "Сайт, мобільний додаток, Telegram та інші канали — один інбокс." },
    { icon: Zap, title: "Real-time", text: "WebSocket-звʼязок з автоматичним fallback на polling." },
    { icon: Shield, title: "Безпечний", text: "API-ключі з можливістю відкликання та JWT для операторів." },
    { icon: Smartphone, title: "Source-agnostic", text: "Кожна пара (source, contact_id) — окремий діалог." },
  ];

  return (
    <div className="h-full overflow-y-auto bg-slate-50 text-slate-900">
      {/* Hero */}
      <header className="relative overflow-hidden">
        <div className="absolute -top-40 -right-24 w-[500px] h-[500px] rounded-full bg-[#0F76BC]/10 blur-3xl pointer-events-none" />
        <div className="max-w-5xl mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-12 relative">
          <span className="inline-block px-3 py-1 rounded-full bg-[#0F76BC]/10 text-[#0F76BC] text-xs font-bold uppercase tracking-[0.2em] mb-6">
            Онлайн-чат підтримки
          </span>
          <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] max-w-3xl">
            Один інбокс для всіх ваших каналів звʼязку
          </h1>
          <p className="text-lg text-slate-600 mt-6 max-w-2xl leading-relaxed">
            IPPLUS Chat обʼєднує звернення з сайту, мобільного додатку та інших ресурсів
            в єдину панель операторів. Підключення одним рядком коду.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <a href="#embed" className="bg-[#0F76BC] hover:bg-[#0c5f96] text-white px-7 py-3.5 rounded-[12px] font-semibold transition-colors flex items-center gap-2 shadow-sm">
              <Code2 size={18} /> Код для сайту
            </a>
          </div>
          <p className="text-sm text-slate-400 mt-6">
            👉 Спробуйте живий віджет — кнопка в правому нижньому куті сторінки.
          </p>
        </div>
      </header>

      {/* Features */}
      <section className="max-w-5xl mx-auto px-5 md:px-10 py-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {features.map((f) => (
          <div key={f.title} className="bg-white border border-slate-200 rounded-[12px] p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-10 h-10 rounded-[10px] bg-[#0F76BC]/10 text-[#0F76BC] flex items-center justify-center mb-3">
              <f.icon size={20} />
            </div>
            <h3 className="font-head font-bold mb-1">{f.title}</h3>
            <p className="text-sm text-slate-500 leading-relaxed">{f.text}</p>
          </div>
        ))}
      </section>

      {/* Embed */}
      <section id="embed" className="max-w-5xl mx-auto px-5 md:px-10 py-12">
        <div className="bg-white border border-slate-200 rounded-[12px] p-6 md:p-8 shadow-sm">
          <h2 className="font-head text-2xl font-bold tracking-tight mb-2">Підключення до сайту</h2>
          <p className="text-slate-600 mb-5">Розмістіть цей рядок перед закриваючим тегом <code className="text-[#0F76BC] font-mono text-sm">&lt;/body&gt;</code>. Файл віджета можна розмістити на власному хостингу.</p>
          <div className="relative">
            <pre data-testid="embed-snippet" className="bg-slate-900 text-slate-100 rounded-[12px] p-5 pr-14 overflow-x-auto text-sm leading-relaxed font-mono">{snippet}</pre>
            <button onClick={copy} data-testid="copy-snippet-btn"
              className="absolute top-3 right-3 w-9 h-9 rounded-[10px] bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors">
              {copied ? <Check size={16} className="text-emerald-400" /> : <Copy size={16} />}
            </button>
          </div>
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-head font-bold mb-2 flex items-center gap-2"><Smartphone size={18} className="text-[#0F76BC]" /> Мобільний додаток</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Використовуйте ті самі public-ендпоінти з <code className="font-mono text-[#0F76BC]">source="app"</code> та
                <code className="font-mono text-[#0F76BC]"> contact_id=UID</code> абонента. Потрібен лише базовий URL сервісу та API-ключ.
              </p>
            </div>
            <div>
              <h3 className="font-head font-bold mb-2 flex items-center gap-2"><Shield size={18} className="text-[#0F76BC]" /> API-ключі</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Створюйте та відкликайте ключі в <Link to="/admin/keys" className="text-[#0F76BC] font-semibold underline">панелі управління</Link>.
                Кожен запит має містити заголовок <code className="font-mono text-[#0F76BC]">X-Chat-Key</code>.
              </p>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white py-8 text-center text-sm text-slate-400">
        IPPLUS Chat — автономний сервіс підтримки. Фірмовий синій #0F76BC.
      </footer>
    </div>
  );
}
